import React from 'react';
import { Contract, PartialEarlySettlement } from 'cpes-common';
import { Card, Layout } from '@vwfs-bronson/bronson-react';
import { TranslationFormat, useTranslationWithFormatting } from '../../../../localization/useTranslationWithFormatting';
import { DefinitionListHorizontal, DefinitionListItem } from '@cp-shared-19/frontend-ui';
import { Moment } from 'moment';
import { formatCpDate } from '@cp-shared-19/common-utilities';

export type ComparisonSectionProps = {
    contractDetails: Contract;
    partialEarlySettlementDetails: PartialEarlySettlement;
};

export const ComparisonSection: React.FC<ComparisonSectionProps> = ({
    contractDetails,
    partialEarlySettlementDetails,
}) => {
    const { t, f } = useTranslationWithFormatting('partial-early-settlement');

    const getClassName = (newValue: Moment | number, oldValue: Moment | number): string => {
        return newValue < oldValue ? 'u-text-success' : '';
    };

    const currentContractInformationList: DefinitionListItem[] = [
        {
            label: t('details.comparison.current.monthly-rate'),
            value: f(contractDetails.nextInstalmentAmount, TranslationFormat.CURRENCY),
        },
        {
            label: t('details.comparison.current.remaining-installments'),
            value: f(partialEarlySettlementDetails.formerRemainingInstallments, TranslationFormat.NUMBER),
        },
        {
            label: t('details.comparison.current.end-date'),
            value: f(partialEarlySettlementDetails.formerEndDate, TranslationFormat.DATE),
        },
        {
            label: t('details.comparison.current.outstanding-capital'),
            value: f(partialEarlySettlementDetails.formerPendingAmount, TranslationFormat.CURRENCY),
        },
    ].filter(item => !!item.value);

    const newContractInformationList: DefinitionListItem[] = [
        {
            label: t('details.comparison.new.monthly-rate'),
            value: (
                <div
                    className={getClassName(
                        partialEarlySettlementDetails.newInstallmentAmount,
                        contractDetails.nextInstalmentAmount ? contractDetails.nextInstalmentAmount : 0,
                    )}
                >
                    {f(partialEarlySettlementDetails.newInstallmentAmount, TranslationFormat.CURRENCY)}
                </div>
            ),
        },
        {
            label: t('details.comparison.new.remaining-installments'),
            value: (
                <div
                    className={getClassName(
                        partialEarlySettlementDetails.remainingInstallments,
                        partialEarlySettlementDetails.formerRemainingInstallments,
                    )}
                >
                    {f(partialEarlySettlementDetails.remainingInstallments, TranslationFormat.NUMBER)}
                </div>
            ),
        },
        {
            label: t('details.comparison.new.end-date'),
            value: (
                <div
                    className={getClassName(
                        formatCpDate(partialEarlySettlementDetails.endDate).toMoment(),
                        formatCpDate(partialEarlySettlementDetails.formerEndDate).toMoment(),
                    )}
                >
                    {f(partialEarlySettlementDetails.endDate, TranslationFormat.DATE)}
                </div>
            ),
        },
        {
            label: t('details.comparison.new.outstanding-capital'),
            value: (
                <div
                    className={getClassName(
                        partialEarlySettlementDetails.pendingAmount,
                        partialEarlySettlementDetails.formerPendingAmount,
                    )}
                >
                    {f(partialEarlySettlementDetails.pendingAmount, TranslationFormat.CURRENCY)}
                </div>
            ),
        },
    ].filter(item => !!item.value);

    return (
        <Card title="">
            <Layout equalHeight divider>
                <Layout.Item default="1/2" s="1/1">
                    <div>
                        <h6>{t('details.comparison.current.headline')}</h6>
                        <DefinitionListHorizontal list={currentContractInformationList} />
                    </div>
                </Layout.Item>
                <Layout.Item default="1/2" s="1/1">
                    <div>
                        <h6>{t('details.comparison.new.headline')}</h6>
                        <DefinitionListHorizontal list={newContractInformationList} />
                    </div>
                </Layout.Item>
            </Layout>
        </Card>
    );
};
